import { Button, CanopyProvider, Dialog } from '@bloomfieldrobotics/canopy';
import { Check } from '@mui/icons-material';
import {
  DialogActions,
  DialogContent,
  Divider,
  Paper,
  Stack,
  StackProps,
  styled,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useInitialOnboardingComplete } from '../../../services/entities/hooks/settings/useInitialOnboardingComplete';
import { usePreferredLanguage } from '../../../services/entities/hooks/settings/usePreferredLanguage';
import { usePreferredUnits } from '../../../services/entities/hooks/settings/usePreferredUnits';
import {
  defaultUserSettings,
  useSettings,
} from '../../../services/entities/hooks/settings/useSettings';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { NativeLanguageSelectionDropdown } from './components/LanguageSelect';
import UnitSelectionDropdown from './components/UnitSelect';

const StyledStack = styled((props: StackProps) => (
  <Stack direction='row' {...props} />
))(({ theme }) => ({
  padding: theme.spacing(2),
  justifyContent: 'space-between',
  fontSize: '0.875',
  fontWeight: 400,
  lineHeight: '142%',
  width: '100%',
  alignItems: 'center',
}));

const InitialSetupDialog = () => {
  const { settings, isSettingsLoading } = useSettings();

  const [open, setOpen] = useState(false);
  const [showConfirmationMessage, setShowConfirmation] = useState(false);

  const { t, setLanguage: setDisplayLanguage } = useTranslation();

  const { preferredUnits, setPreferredUnits } = usePreferredUnits();
  const { preferredLanguage, setPreferredLanguage } = usePreferredLanguage();

  const { setInitialOnboardingComplete } = useInitialOnboardingComplete();

  const handleLanguageChange = useCallback(
    (language: string) => {
      setDisplayLanguage(language);
      setPreferredLanguage(language);
    },
    [setDisplayLanguage, setPreferredLanguage]
  );

  useEffect(() => {
    if (!isSettingsLoading && !settings?.onboardingComplete) {
      setOpen(true);
      // If the preferred language or units are not set, set them to the default
      if (!preferredLanguage) {
        setPreferredLanguage(defaultUserSettings.language);
      }
      if (!preferredUnits) {
        setPreferredUnits(defaultUserSettings.units);
      }
    }
  }, [
    settings?.onboardingComplete,
    isSettingsLoading,
    preferredLanguage,
    preferredUnits,
    setPreferredLanguage,
    setPreferredUnits,
  ]);

  const handleClose = () => {
    setInitialOnboardingComplete(true);
    setShowConfirmation(true);
    setTimeout(() => {
      setOpen(false);
      setTimeout(() => {
        setShowConfirmation(false);
      }, 200);
    }, 1500);
  };

  return (
    <CanopyProvider>
      <Dialog open={open}>
        <DialogContent>
          {showConfirmationMessage ? (
            <Stack direction='row' spacing={2}>
              <Check color='success' />
              <Typography>
                {t('You can edit your choices later in the settings menu.')}
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={4}>
              <Typography variant='h6'>Welcome!</Typography>
              <Typography>
                {t(
                  'Please select your preferred language and unit system below.'
                )}
              </Typography>
              <Paper
                sx={{
                  padding: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  width: '100%',
                }}
              >
                <StyledStack>
                  <Typography>Language</Typography>
                  <NativeLanguageSelectionDropdown
                    onChange={handleLanguageChange}
                    value={preferredLanguage ?? defaultUserSettings.language}
                  />
                </StyledStack>
                <Divider />
                <StyledStack>
                  <Typography>Units of Measurements</Typography>
                  <UnitSelectionDropdown
                    value={preferredUnits ?? defaultUserSettings.units}
                    onChange={setPreferredUnits}
                  />
                </StyledStack>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        {!showConfirmationMessage && (
          <DialogActions>
            <Button
              variant='contained'
              size='large'
              color='primary'
              sx={{
                width: '100%',
                maxWidth: '10rem',
                whiteSpace: 'nowrap',
              }}
              onClick={handleClose}
            >
              {t('Done')}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </CanopyProvider>
  );
};

export default InitialSetupDialog;
